import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth, usePageTitle } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Brain, Send, User, Bot, Lock, Crown, Sparkles, Play, ExternalLink, Network } from 'lucide-react';
import { toast } from 'sonner';
import ForceGraph2D from 'react-force-graph-2d';

export default function AnalyzerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  usePageTitle('AI In Toto Analyzer');
  
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "I am the In Toto Analyzer. Ask me how different phenomena, people, and events are connected. I'll show you the whole picture with visual graphs and the latest research videos.",
      graph_data: null,
      related_videos: []
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [latestVideos, setLatestVideos] = useState([]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch latest videos on mount
  useEffect(() => {
    const fetchLatestVideos = async () => {
      try {
        const res = await axios.get(`${API}/youtube/latest`);
        setLatestVideos(res.data.videos || []);
      } catch (error) {
        console.error('Error fetching videos:', error);
      }
    };
    fetchLatestVideos();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    if (!user) {
      toast.error('Please sign in to use the AI Analyzer');
      navigate('/auth');
      return;
    }

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/ai/analyze`,
        { message: input },
        { 
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true 
        }
      );
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: res.data.response,
        graph_data: res.data.graph_data,
        related_videos: res.data.related_videos || []
      }]);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error analyzing. Please try again.';
      
      if (error.response?.status === 403) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "You've reached your daily AI limit. Upgrade to Premium for unlimited access to the In Toto Analyzer.",
          graph_data: null,
          related_videos: []
        }]);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const suggestedQuestions = [
    "How does Chris Bledsoe connect to government programs?",
    "What is the Theory of Wow and how does it explain UFO encounters?",
    "How does Geometric Unity relate to consciousness research?",
    "What evidence links Skinwalker Ranch to the hitchhiker effect?"
  ];

  const nodeColor = (node) => {
    return node.type === 'person' ? '#00F0FF' : '#FFD700';
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <div className="py-8 px-4 md:px-8 border-b border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Brain className="w-8 h-8 text-cyan-400" />
            <h1 className="font-heading text-2xl md:text-3xl font-bold" data-testid="analyzer-title">
              AI "In Toto" Analyzer
            </h1>
          </div>
          <p className="text-white/50">
            Ask how phenomena, people, and events interconnect. Powered by GPT-5.2 with visual connection graphs.
          </p>
          {user && !user.is_premium && (
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm flex items-center gap-3">
              <Crown className="w-5 h-5 text-yellow-500" />
              <span className="text-sm text-yellow-500/90">Free tier: 5 questions per day.</span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-yellow-500 hover:text-yellow-400 ml-auto"
                onClick={() => navigate('/subscription')}
              >
                Upgrade
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row">
        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 overflow-auto py-8 px-4 md:px-8">
            <div className="max-w-4xl mx-auto space-y-6" data-testid="chat-messages">
              {messages.map((msg, idx) => (
                <div key={idx}>
                  <div className={`flex items-start gap-4 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === 'user' ? 'bg-cyan-400/20' : 'bg-white/10'
                    }`}>
                      {msg.role === 'user' ? (
                        <User className="w-5 h-5 text-cyan-400" />
                      ) : (
                        <Bot className="w-5 h-5 text-white/70" />
                      )}
                    </div>
                    <div className={`flex-1 card-dark p-5 ${msg.role === 'user' ? 'bg-cyan-400/5' : ''}`}>
                      <p className="text-white/80 leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                      
                      {/* Connection Graph */}
                      {msg.graph_data && msg.graph_data.nodes?.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <div className="flex items-center gap-2 mb-3 text-cyan-400">
                            <Network className="w-4 h-4" />
                            <span className="text-sm font-mono uppercase">Connection Map</span>
                          </div>
                          <div className="h-64 bg-black/30 rounded-sm overflow-hidden">
                            <ForceGraph2D
                              graphData={msg.graph_data}
                              nodeColor={nodeColor}
                              nodeLabel="name"
                              nodeRelSize={8}
                              linkColor={() => 'rgba(255,255,255,0.2)'}
                              linkWidth={2}
                              width={500}
                              height={250}
                              backgroundColor="transparent"
                            />
                          </div>
                        </div>
                      )}

                      {/* Related Videos */}
                      {msg.related_videos?.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <div className="flex items-center gap-2 mb-3 text-cyan-400">
                            <Play className="w-4 h-4" />
                            <span className="text-sm font-mono uppercase">Related Videos</span>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {msg.related_videos.map((video, vidIdx) => (
                              <a
                                key={vidIdx}
                                href={video.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group block"
                              >
                                <div className="relative aspect-video rounded-sm overflow-hidden mb-2">
                                  <img 
                                    src={video.thumbnail} 
                                    alt={video.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                  />
                                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Play className="w-8 h-8 text-white" />
                                  </div>
                                </div>
                                <p className="text-xs text-white/70 line-clamp-2 group-hover:text-cyan-400 transition-colors">
                                  {video.title}
                                </p>
                                <p className="text-xs text-white/40 mt-1">@{video.channel}</p>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-5 h-5 text-white/70" />
                  </div>
                  <div className="card-dark p-5">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                      <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Suggested Questions */}
          {messages.length <= 1 && (
            <div className="px-4 md:px-8 pb-4">
              <div className="max-w-4xl mx-auto">
                <p className="text-white/40 text-sm mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Suggested questions
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {suggestedQuestions.map((q, idx) => (
                    <button
                      key={idx}
                      onClick={() => setInput(q)}
                      className="text-left p-3 text-sm text-white/60 bg-white/5 border border-white/10 rounded-sm hover:border-cyan-400/50 hover:text-white/80 transition-colors"
                      data-testid={`suggested-${idx}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="py-6 px-4 md:px-8 border-t border-white/5">
            <div className="max-w-4xl mx-auto">
              {!user ? (
                <div className="text-center py-4">
                  <Lock className="w-8 h-8 mx-auto mb-3 text-white/30" />
                  <p className="text-white/50 mb-4">Sign in to use the AI Analyzer</p>
                  <Button onClick={() => navigate('/auth')} className="btn-primary" data-testid="signin-btn">
                    Sign In
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex gap-4">
                  <Input
                    type="text"
                    placeholder="Ask how things connect in toto..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    disabled={loading}
                    data-testid="chat-input"
                  />
                  <Button 
                    type="submit" 
                    className="btn-primary flex items-center gap-2" 
                    disabled={loading || !input.trim()}
                    data-testid="send-btn"
                  >
                    <Send className="w-4 h-4" />
                    Send
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar - Latest Videos */}
        <div className="w-full lg:w-80 border-t lg:border-t-0 lg:border-l border-white/5 p-4 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Play className="w-5 h-5 text-cyan-400" />
            <h3 className="font-mono text-sm uppercase tracking-wider text-white/60">Latest Research</h3>
          </div>
          
          <div className="space-y-4 max-h-[600px] overflow-y-auto">
            {latestVideos.length === 0 ? (
              <div className="text-white/30 text-sm text-center py-8">
                Loading videos...
              </div>
            ) : (
              latestVideos.slice(0, 8).map((video, idx) => (
                <a
                  key={idx}
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group"
                  data-testid={`video-${idx}`}
                >
                  <div className="relative aspect-video rounded-sm overflow-hidden mb-2">
                    <img 
                      src={video.thumbnail} 
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Play className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/80 rounded text-xs text-white/80">
                      @{video.channel}
                    </div>
                  </div>
                  <p className="text-sm text-white/70 line-clamp-2 group-hover:text-cyan-400 transition-colors">
                    {video.title}
                  </p>
                </a>
              ))
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="text-xs text-white/30 text-center">
              Videos from @JesseMichels, @JeremyCorbell, @UAPGerb
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
